import type {ActionReducerMapBuilder} from '@reduxjs/toolkit';
import type {AllEventsMap, EventHashedID, EventsReduxState, EventStatusFilterMap, FetchEventsFromServerParams} from '@Components/events/events.types';
import {UserEventCreationPermissionState} from '@Components/events/events.types';
import {deleteEvent, fetchDoesUserNeedEventCreationUpsell, getEventsList, hideEmbedEventsScheduleBanner, loadEventsCalendarSectionData, loadEventsPageMetadata, toggleAllowRegistrationsForEvent, toggleShareWithTeam} from '@Components/events/events-thunk';
import {LoadingStates} from '@Utils/loading.util';
import {EVENTS_LAZY_BATCH_SIZE, getEventsLazyDataBasedOnAppliedFilters, getSortedEventsBasedOnLiveStatus} from '@Libraries/events.library';
import {objectArrayToHashmap, objectsEqual, removeItem} from '@Utils/array.util';

export const eventsExtraReducers = (builder: ActionReducerMapBuilder<EventsReduxState>): void => {
  builder.addCase(fetchDoesUserNeedEventCreationUpsell.fulfilled, (state, {payload}) => {
    state.userEventCreationPermissionState = payload ? UserEventCreationPermissionState.NEEDS_UPSELL : UserEventCreationPermissionState.CAN_CREATE;
  });

  builder.addCase(getEventsList.pending, (state): void => {
    const lazyData = getEventsLazyDataBasedOnAppliedFilters(state, state.eventsViewType, state.filters);
    lazyData.loadingState = LoadingStates.LOADING;
  });

  builder.addCase(getEventsList.fulfilled, (state, {meta, payload}) => {
    if (isAjaxResponseOutdated(state, meta.arg)) {
      return;
    }

    const lazyData = getEventsLazyDataBasedOnAppliedFilters(state, state.eventsViewType, state.filters);
    lazyData.loadingState = LoadingStates.LOADED;
    const sortedEvents = getSortedEventsBasedOnLiveStatus(payload);
    const existing = new Set(lazyData.ids);
    const newIds = sortedEvents.map(e => e.hashedID).filter(id => !existing.has(id));
    lazyData.ids = lazyData.ids.concat(newIds);
    lazyData.loadMore = newIds.length >= EVENTS_LAZY_BATCH_SIZE;
    state.allEvents = {...state.allEvents, ...objectArrayToHashmap(payload, 'hashedID')};
  });

  builder.addCase(toggleShareWithTeam.fulfilled, (state, {meta}) => {
    const event = state.allEvents[meta.arg];
    if (event) {
      event.isShared = !event.isShared;
    }
  });

  builder.addCase(toggleAllowRegistrationsForEvent.fulfilled, (state, {meta, payload}) => {
    const event = state.allEvents[meta.arg];
    if (event) {
      event.allowRegistrations = payload;
    }
  });

  builder.addCase(deleteEvent.fulfilled, (state, {meta}) => {
    removeEventFromStore(state, meta.arg);
  });

  builder.addCase(loadEventsPageMetadata.pending, (state) => {
    state.pageMetaDataLoadingState = LoadingStates.LOADING;
  });

  builder.addCase(loadEventsPageMetadata.rejected, (state) => {
    state.pageMetaDataLoadingState = LoadingStates.LOADED;
  });

  builder.addCase(loadEventsPageMetadata.fulfilled, (state, {payload}) => {
    state.userEventCreationPermissionState = payload.doShowUpsell ? UserEventCreationPermissionState.NEEDS_UPSELL : UserEventCreationPermissionState.CAN_CREATE;
    state.doShowEmbedBanner = payload.doShowEmbedBanner;
    state.doShowStripeTab = payload.doShowStripeTab;
    state.pageMetaDataLoadingState = LoadingStates.LOADED;
  });

  builder.addCase(loadEventsCalendarSectionData.pending, (state) => {
    state.calendarSectionLoadingState = LoadingStates.LOADING;
  });

  builder.addCase(loadEventsCalendarSectionData.rejected, (state) => {
    state.calendarSectionLoadingState = LoadingStates.LOADED;
  });

  builder.addCase(loadEventsCalendarSectionData.fulfilled, (state, {payload}) => {
    state.calendarSectionLoadingState = LoadingStates.LOADED;
    state.eventCalendarMetaData = payload.eventCalendarMetaData;
  });

  builder.addCase(hideEmbedEventsScheduleBanner.pending, (state) => {
    state.doShowEmbedBanner = false;
  })
};

const isAjaxResponseOutdated = (state: EventsReduxState, ajaxParams: FetchEventsFromServerParams): boolean => {
  return (
    state.eventsViewType !== ajaxParams.eventsViewType || !areStatusFiltersSame(state.filters.statusFilter, ajaxParams.statusFilters) || ajaxParams.sort !== state.filters.sortType
  );
};

const areStatusFiltersSame = (statusFilters1: EventStatusFilterMap, statusFilter2: EventStatusFilterMap): boolean => {
  return objectsEqual(statusFilters1, statusFilter2);
};

export const removeEventFromStore = (state: EventsReduxState, eventHashedID: EventHashedID): void => {
  state.eventsListDefaultLazyData.ids = removeItem(state.eventsListDefaultLazyData.ids, eventHashedID);
  state.eventsListFilteredLazyData.ids = removeItem(state.eventsListFilteredLazyData.ids, eventHashedID);
  const newEventsMap: AllEventsMap = {};
  Object.entries(state.allEvents).forEach(([hashedID, event]) => {
    if (event.hashedID !== eventHashedID) {
      newEventsMap[hashedID] = event;
    }
  });

  state.allEvents = newEventsMap;
}
